<script setup lang="ts">
const props = defineProps({
  collectible: {
    type: Object as PropType<Collectible>,
    required: true,
  },
});
const {
  getCollectibleHighestOffer,
  getUserOfferOnCollectible,
  setCollectibleAsViewed,
} = useCollectiblesController();
const { t } = useI18n();
const appStateStore = useAppStateStore();
const route = useRoute();
const userStore = useUserStore();
const offer = ref<Offer | null>(null);
const showOfferBanner = ref<boolean>(false);
const { hideRecommendedPrice } = useRuntimeConfig().public;

const checkOffers = async () => {
  showOfferBanner.value = false;
  offer.value = null;
  if (!userStore.isLoggedIn) return;
  if (userStore.isCurrentUserId(props.collectible.gfalId)) {
    const res = await getCollectibleHighestOffer(props.collectible.id);
    if (res) {
      offer.value = res;
      showOfferBanner.value = true;
    }
  } else {
    const res = await getUserOfferOnCollectible(
      props.collectible.id,
      userStore.id
    );
    if (res) {
      offer.value = res;
      showOfferBanner.value = true;
    }
  }
};

const priceText = computed(() => {
  if (props.collectible.id === "default-jewel") return "";

  return userStore.isCurrentUserId(props.collectible.gfalId) &&
    props.collectible.price?.GFAL
    ? t("main.cardCollections.listedFor")
    : props.collectible.price
    ? t("main.cardCollectible.buyNow")
    : t("main.cardCollections.recommendedPrice");
});

const showTag = computed(
  () =>
    route.name === "collection-detail" ||
    route.name === "public-collectible-detail"
);

const setOwnCollectibleAsSeen = async () => {
  if (
    userStore.isCurrentUserId(props.collectible.gfalId) &&
    props.collectible.new
  ) {
    await setCollectibleAsViewed(props.collectible.id);
  }
};

const init = () => {
  checkOffers();
  setOwnCollectibleAsSeen();
  appStateStore.currentMerchant = props.collectible.merchantId as MerchantId;
};

onMounted(() => {
  init();
});

const showRecommendedPrice = computed(() => {
  if (!userStore.isCurrentUserId(props.collectible.gfalId)) return false;
  if (
    !props.collectible.price?.GFAL ||
    !props.collectible.recommendedPrice.GFAL
  )
    return false;
  return true;
});

const { getMerchantAttributes } = useLuxuryDesign();
const collectibleDesign = computed(() =>
  getMerchantAttributes(props.collectible.merchantId)
);
</script>
<template>
  <HeadlessTransitionRoot
    :show="showOfferBanner"
    enter="transition-opacity duration-1000"
    enter-from="opacity-0"
    enter-to="opacity-100"
    leave="transition-opacity duration-1000"
    leave-from="opacity-100"
    leave-to="opacity-0">
    <OffersBanner
      v-if="offer"
      :offer="offer!"
      :collectible="collectible"
      @offer-handled="checkOffers" />
  </HeadlessTransitionRoot>
  <div
    v-bind="collectibleDesign"
    class="border-2 border-primary-900 rounded-lg merchant-card">
    <div class="xl:grid xl:grid-cols-2">
      <CardDetailImage
        :merchantId="collectible.merchantId"
        :rarity="collectible.rarity"
        :image-id="collectible.imageId"
        :properties="collectible.properties[collectible.merchantId]"
        :listed="showTag && Boolean(collectible.price?.GFAL)"
        :is-new="
          userStore.isCurrentUserId(props.collectible.gfalId) && collectible.new
        "
        :number="collectible.number ? collectible.number : undefined"
        :level="collectible.level ? collectible.level : undefined" />
      <div
        class="relative xl:col-start-2 xl:row-span-3 flex flex-col border-t border-l-0 xl:border-l xl:border-t-0 border-primary-900">
        <CardDetailShare />
        <CardDetailTitle
          :id="collectible.id"
          :translation-id="collectible.translationId"
          :merchantId="collectible.merchantId"
          :categoryId="collectible.categoryId"
          :number="collectible.number ?? 0"
          :total="collectible.supply ?? 0" />
        <CardDetailOwner :gfal-id="collectible.gfalId" />
        <div
          v-if="collectible.number !== undefined || collectible.level !== undefined"
          class="px-7 py-4 border-b-2 border-primary-900">
          <CardDetailMeta
            :number="collectible.number ?? undefined"
            :level="collectible.level ?? undefined" />
        </div>
        <CardDetailDescription
          :translationId="collectible.translationId"
          :merchantId="collectible.merchantId" />
        <CardDetailProperties
          v-if="collectible.merchantId !== 'proof-of-luxe'"
          :rarity="collectible.rarity"
          :image-id="collectible.imageId"
          :merchant-id="collectible.merchantId"
          :properties="collectible.properties[collectible.merchantId]" />
        <CardDetailPrice
          v-if="
            collectible.price?.GFAL ||
            hideRecommendedPrice.toString() !== 'true'
          "
          :price="collectible.price"
          :recommended-price="collectible.recommendedPrice"
          :text="priceText"
          :show-recommended-price="showRecommendedPrice" />
        <div v-else class="p-4"></div>
        <CardDetailListButtons
          v-if="userStore.isCurrentUserId(props.collectible.gfalId)"
          :collectible="collectible" />
        <div
          v-else-if="collectible.id !== 'default-jewel'"
          class="flex flex-wrap gap-4 pl-7 item-detail__actions">
          <CardDetailBuyButton :collectible="collectible" />
          <CardDetailOfferMakeButton
            :disabled="Boolean(offer)"
            :collectible="collectible"
            @offer-modal-closed="checkOffers" />
        </div>
      </div>
    </div>
  </div>
  <CardDetailStats
    :stats="collectible.properties[collectible.merchantId]"
    :merchant-id="collectible.merchantId" />
  <CardDetailLegacy
    :stats="collectible.properties[collectible.merchantId]"
    :merchant-id="collectible.merchantId" />
  <CardDetailOwnershipHistory
    v-if="collectible.id === 'default-jewel'"
    :id="'GFAL'" />
  <CardDetailOwnershipHistory v-else :id="collectible.id" />
</template>

<style scoped>
.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
